import FormHelperText from '@mui/material/FormHelperText';
import { css, styled, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { humanFileSize } from '@openx/utils/lib/humanFileSize.ts';
import { CloudArrowUpIcon } from '@phosphor-icons/react/CloudArrowUp';
import { FileTextIcon } from '@phosphor-icons/react/FileText';
import { type JSX, type MouseEvent, useCallback } from 'react';
import { type Accept, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button/Button.tsx';

const sharedIconStyles = (theme: Theme) => css`
  font-size: 2em;
  margin-right: ${theme.spacing(1)};
`;

const StyledUploadHintsDiv = styled('div')`
  align-items: center;
  display: flex;

  & > p {
    align-items: center;
    display: inline-flex;
  }

  & > span {
    margin: ${({ theme }) => theme.spacing(0, 2)};
  }
`;

const StyledCloudUploadIcon = styled(CloudArrowUpIcon)`
  ${({ theme }) => sharedIconStyles(theme)}
  color: ${({ theme }) => theme.palette.primary.main};
`;

const StyledDescriptionIcon = styled(FileTextIcon)`
  ${({ theme }) => sharedIconStyles(theme)}
  color: ${({ theme }) => theme.palette.primary.main};
`;

const StyledFileInfoDiv = styled('div')`
  align-items: center;
  display: flex;

  & > p {
    margin-right: ${({ theme }) => theme.spacing(1.2)};
  }
`;

const StyledWrapperDiv = styled('div')`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const StyledDropzoneDiv = styled('div')<{ disabled: boolean; hasFile: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.palette.background.boxLight};
  border: 1px solid ${({ theme }) => theme.palette.background.grayLight};
  border-radius: 2px;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  height: 82px;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  &:focus {
    outline: none;
  }

  ${({ hasFile, theme }) =>
    hasFile &&
    css`
      justify-content: space-between;
      padding: ${theme.spacing(0, 2.2)};
    `}
`;

interface DropzoneProps {
  accept?: Accept;
  disabled?: boolean;
  file?: File | null;
  onDropFile: (file: File) => void;
  onCancel: () => void;
  className?: string;
  errorMessage?: string;
  helpMessage?: string;
}

export const Dropzone = ({
  accept,
  disabled = false,
  onDropFile,
  onCancel,
  file,
  className,
  errorMessage,
  helpMessage,
}: DropzoneProps): JSX.Element => {
  const { t } = useTranslation();

  const onDrop = useCallback(
    (uploadedFiles: File[]) => {
      onDropFile(uploadedFiles[0]);
    },
    [onDropFile]
  );

  const { getRootProps, getInputProps } = useDropzone({ accept, disabled, multiple: false, onDrop });

  const onCancelCallback = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      onCancel();
    },
    [onCancel]
  );

  const renderContent = (): JSX.Element => {
    if (!file) {
      return (
        <StyledUploadHintsDiv>
          <StyledCloudUploadIcon data-test="upload-icon" />
          <Typography variant="caption" component="p" data-test="drag-n-drop-text">
            {t('DROP FILE HERE')}
          </Typography>
          <span>{t('or')}</span>
          <Button data-test="browse-button" size="small">
            {t('Browse')}
          </Button>
        </StyledUploadHintsDiv>
      );
    }

    return (
      <>
        <StyledFileInfoDiv>
          <StyledDescriptionIcon data-test="description-icon" />
          <Typography variant="body1" color="textSecondary">
            {humanFileSize(file.size)}
          </Typography>
          <Typography variant="body1">{file.name}</Typography>
        </StyledFileInfoDiv>
        <Button variant="text" color="error" onClick={onCancelCallback} data-test="remove-button">
          {t('REMOVE')}
        </Button>
      </>
    );
  };

  return (
    <StyledWrapperDiv className={className}>
      <StyledDropzoneDiv {...getRootProps()} hasFile={!!file} disabled={disabled} data-test="drop-zone">
        <input data-test="file-input" {...getInputProps()} />
        {renderContent()}
      </StyledDropzoneDiv>

      {helpMessage && !errorMessage && <FormHelperText>{helpMessage}</FormHelperText>}
      {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
    </StyledWrapperDiv>
  );
};
